import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { FeedbackKeyPanel } from './FeedbackKeyPanel';
import { OpenaiConnection } from './OpenaiConnection';
import { useCompany, useCompanyId } from './useCompany';
import { m } from 'motion/react';
import { KeyRound } from 'lucide-react';

import { companies, credentials, type Credential } from '../../api/endpoints';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Card, PageHeader } from '../../components/Surface';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { toast } from '../../components/Toast';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';

const TYPES = ['anthropic_api_key', 'claude_subscription_oauth', 'openai_api_key', 'github_pat'] as const;

type CredentialKind = (typeof TYPES)[number];

/**
 * `13 §2`: GitHub and Claude credentials.
 *
 * The secret input is write-only and never repopulated — the API does not return
 * secrets (control S-02) and a field that looked pre-filled would be a lie about
 * what the server holds.
 *
 * `github_app_installation` is deliberately absent from the list: it is
 * configured through the App install flow, which answers 501 until phase 5, so
 * offering it would only produce a rejection nobody could act on.
 */
export function CredentialsScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const { busy, error, fields, submit } = useSubmit<void>();
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<CredentialKind>(TYPES[0]);

  const list = useQuery({
    queryKey: ['credentials', companyId],
    queryFn: () => credentials.list(companyId),
    enabled: companyId !== '',
  });

  const invalidate = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['credentials', companyId] }), queryClient.invalidateQueries({ queryKey: ['company', companyId] })]);

  const verify = useMutation({
    mutationFn: (id: string) => credentials.verify(companyId, id),
    onSuccess: () => void invalidate(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => credentials.revoke(companyId, id),
    onSuccess: () => void invalidate(),
  });

  // Which credential of a type is *the* one is not decided when it is added and
  // never again: keys get rotated, a subscription is added later, one of the two
  // is being tried out. Without this the only way to change it was to revoke and
  // re-paste a secret the issuer shows once.
  const setDefault = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      credentials.update(companyId, id, { is_default: value }),
    onSuccess: () => void invalidate(),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(
      async () => {
        await credentials.create(companyId, {
          type: String(form.get('type') ?? ''),
          label: String(form.get('label') ?? ''),
          secret: String(form.get('secret') ?? ''),
          is_default: form.get('is_default') === 'on',
        });

        await invalidate();
      },
      () => setAdding(false),
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('company.tabs.credentials')}
        description={t('credentials.intro')}
        actions={!adding ? <Button onClick={() => setAdding(true)}>{t('credentials.add')}</Button> : null}
      />

      {adding ? (
        <m.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg space-y-4 rounded-lg border border-line bg-raised p-5 shadow-sm"
        >
          {error ? <ErrorBanner error={error} /> : null}

          <div className="space-y-1.5">
            <label htmlFor="type" className="block text-sm font-semibold text-ink">
              {t('credentials.type')}
            </label>
            <select
              id="type"
              name="type"
              value={type}
              onChange={(event) => setType(event.target.value as CredentialKind)}
              className={cn(fieldStyles, 'border-line')}
            >
              {TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`credentials.types.${option}` as Key)}
                </option>
              ))}
            </select>
          </div>

          <TypeGuide type={type} />

          <Field
            label={t('credentials.label')}
            name="label"
            required
            placeholder={t('credentials.labelPlaceholder')}
            error={fields.label}
          />
          <Field
            label={t('credentials.secret')}
            name="secret"
            type="password"
            required
            autoComplete="off"
            hint={t('credentials.secretHint')}
            error={fields.secret}
          />

          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
            <input type="checkbox" name="is_default" defaultChecked className="size-4 accent-primary" />
            {t('credentials.useAsDefault')}
          </label>

          <div className="flex gap-2">
            <Button type="submit" busy={busy}>
              {t('credentials.checkAndSave')}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </m.form>
      ) : null}

      <FeedbackKeyPanel key={`feedback-${companyId}`} companyId={companyId} />
      <OpenaiConnection key={companyId} rows={list.data?.data ?? []} />
      <RunPreference credentials={list.data?.data ?? []} />
      <RunPreference credentials={list.data?.data ?? []} provider="openai" />

      {list.isPending ? <SkeletonRows rows={2} /> : null}
      {list.error ? <ErrorBanner error={list.error} onRetry={() => void list.refetch()} /> : null}

      {list.data?.data.length === 0 && !adding ? (
        <EmptyState
          icon={<KeyRound />}
          title={t('credentials.empty')}
          description={t('credentials.emptyHint')}
          action={<Button onClick={() => setAdding(true)}>{t('credentials.add')}</Button>}
        />
      ) : null}

      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised">
        {list.data?.data.map((credential) => (
          <CredentialRow
            key={credential.id}
            credential={credential}
            verifying={verify.isPending && verify.variables === credential.id}
            promoting={setDefault.isPending && setDefault.variables?.id === credential.id}
            result={verify.data?.credential.id === credential.id ? verify.data.check : null}
            onVerify={() => verify.mutate(credential.id)}
            onRevoke={() => revoke.mutate(credential.id)}
            onDefault={(value) => setDefault.mutate({ id: credential.id, value })}
          />
        ))}
      </ul>
    </div>
  );
}

/** Per-provider billing preferences apply to tasks and every profile assistant. */
function RunPreference({ credentials: rows, provider = 'anthropic' }: { credentials: Credential[]; provider?: 'anthropic' | 'openai' }) {
  const t = useT();
  const id = useCompanyId();
  const company = useCompany();
  const queryClient = useQueryClient();
  const { busy, error, submit } = useSubmit<void>();

  const usable = (type: Credential['type']) =>
    rows.some((row) => row.type === type && row.is_default && row.status === 'active');

  if (provider === 'anthropic' ? (!usable('anthropic_api_key') || !usable('claude_subscription_oauth')) : (!usable('openai_api_key') && !usable('openai_subscription_oauth'))) {
    return null;
  }

  const settings = company.data?.settings as Record<string, unknown> | undefined;
  const preferences = settings?.ai_credentials as Record<string, { preferred: 'api_key' | 'subscription'; allow_fallback: boolean }> | undefined;
  const current = preferences?.[provider]?.preferred ?? (provider === 'anthropic' && settings?.run_credential_preference === 'subscription' ? 'subscription' : 'api_key');
  const fallback = preferences?.[provider]?.allow_fallback ?? provider === 'anthropic';

  const choose = (value: 'api_key' | 'subscription') => {
    if (value === current) {
      return;
    }

    void submit(async () => {
      await companies.updateSettings(id, provider === 'anthropic' && !preferences?.anthropic ? { run_credential_preference: value } : { ai_credentials: { [provider]: { preferred: value, allow_fallback: fallback } } });
      await queryClient.invalidateQueries({ queryKey: ['company', id] });
      toast.success(t('settings.account.saved'));
    });
  };

  return (
    <Card size="sm" className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-ink"><span>{provider === 'openai' ? 'OpenAI · ' : 'Claude · '}</span><span>{t('credentials.runPreference.title')}</span></p>
        <p className="text-xs text-ink-soft">{t('credentials.runPreference.intro')}</p>
      </div>

      {error ? <ErrorBanner error={error} /> : null}

      <div className="space-y-2">
        {(['api_key', 'subscription'] as const).map((option) => (
          <label key={option} className="flex cursor-pointer items-start gap-2 text-sm text-ink">
            <input
              type="radio"
              name={`credential_preference_${provider}`}
              value={option}
              checked={current === option}
              disabled={busy}
              onChange={() => choose(option)}
              className="mt-0.5 size-4 accent-primary"
            />
            <span>
              <span className="font-semibold">{t(`credentials.runPreference.${option}` as Key)}</span>{' '}
              <span className="text-ink-soft">{t(`credentials.runPreference.${option}Hint` as Key)}</span>
            </span>
          </label>
        ))}
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={fallback} disabled={busy} onChange={(event) => {
          const value = event.target.checked;
          void submit(async () => {
            await companies.updateSettings(id, { ai_credentials: { [provider]: { preferred: current, allow_fallback: value } } });
            await queryClient.invalidateQueries({ queryKey: ['company', id] });
          });
        }} />
        {t('credentials.openai.fallback')}
      </label>
      <p className="text-xs text-ink-faint">{t('credentials.runPreference.assistant')}</p>
    </Card>
  );
}

/**
 * Where the secret the field below asks for actually comes from, for the type
 * currently selected.
 *
 * Inside the form rather than in the field's hint because everything worth
 * knowing changes with the dropdown: where it is issued, what shape it has and
 * what choosing it costs. The alternative — one generic note — sends a manager
 * out of the screen at the exact moment the form is already open, and half of
 * these secrets are shown by their issuer only once.
 *
 * The `key` on the container is deliberate: switching type remounts the block
 * so it animates in again, which is what makes it read as different text rather
 * than as the same paragraph nobody looks at twice.
 */
function TypeGuide({ type }: { type: CredentialKind }) {
  const t = useT();
  const guide = `credentials.guides.${type}` as const;

  return (
    <m.div
      key={type}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-2 rounded-sm border border-line bg-muted p-3.5 text-xs leading-relaxed text-ink-soft"
    >
      <p>{t(`${guide}.what` as Key)}</p>

      <ol className="list-decimal space-y-1 pl-4">
        <li>{t(`${guide}.step1` as Key)}</li>
        <li>{t(`${guide}.step2` as Key)}</li>
        <li>{t(`${guide}.step3` as Key)}</li>
      </ol>

      <p>
        {t('credentials.guides.shapeLabel')}{' '}
        <span className="font-mono text-ink">{t(`${guide}.shape` as Key)}</span>
      </p>

      <p className="text-ink-faint">{t(`${guide}.note` as Key)}</p>
    </m.div>
  );
}

function CredentialRow({
  credential,
  verifying,
  promoting,
  result,
  onVerify,
  onRevoke,
  onDefault,
}: {
  credential: Credential;
  verifying: boolean;
  promoting: boolean;
  result: { valid: boolean; provider_answered: boolean; error: string | null } | null;
  onVerify: () => void;
  onRevoke: () => void;
  onDefault: (value: boolean) => void;
}) {
  const t = useT();

  return (
    <li className="space-y-2 px-4 py-3 transition-colors hover:bg-hover">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold text-ink">{credential.label}</span>
        <span className="font-mono text-xs text-ink-faint">{credential.hint}</span>
        <Badge tone="neutral" size="sm">
          {t(`credentials.types.${credential.type}` as Key)}
        </Badge>
        {credential.is_default ? (
          <Badge tone="primary" size="sm">
            {t('credentials.default')}
          </Badge>
        ) : null}
        <span className="grow" />
        <Badge tone={credential.status === 'active' ? 'success' : 'danger'} size="sm">
          {t(`credentials.status.${credential.status}` as Key)}
        </Badge>
        {/* Only for an `active` credential, and only in the direction that
            exists: promoting a revoked or invalid one is a 409 server-side
            (`12 §3`), so offering it would be offering a rejection. */}
        {credential.status === 'active' ? (
          <Button variant="ghost" size="sm" busy={promoting} onClick={() => onDefault(!credential.is_default)}>
            {t(credential.is_default ? 'credentials.unsetDefault' : 'credentials.makeDefault')}
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" busy={verifying} onClick={onVerify}>
          {t('credentials.check')}
        </Button>
        {credential.status !== 'revoked' ? (
          <Button variant="danger-ghost" size="sm" onClick={onRevoke}>
            {t(credential.type === 'openai_subscription_oauth' ? 'credentials.openai.disconnect' : 'credentials.revoke')}
          </Button>
        ) : null}
      </div>

      {credential.status !== 'revoked' && credential.type === 'openai_api_key' ? <RotateCredential credential={credential} /> : null}
      {/* The three outcomes are shown as three different things on purpose:
          "the provider said no" and "we could not ask" mean opposite things, and
          telling a manager their working key is broken because of an outage is
          how a good credential gets rotated for nothing. */}
      {result ? (
        <m.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'text-xs',
            result.valid ? 'text-success' : result.provider_answered ? 'font-semibold text-danger' : 'text-ink-soft',
          )}
        >
          {result.valid
            ? t('credentials.accepted')
            : result.provider_answered
              ? t('credentials.rejected', { reason: result.error ?? t('credentials.noReason') })
              : t('credentials.unreachable')}
        </m.p>
      ) : null}
    </li>
  );
}

function RotateCredential({ credential }: { credential: Credential }) {
  const t = useT();
  const companyId = useCompanyId();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const { busy, error, submit } = useSubmit<void>();
  if (!open) return <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>{t('credentials.rotate')}</Button>;
  return <form className="space-y-2" onSubmit={(event) => {
    event.preventDefault();
    const secret = String(new FormData(event.currentTarget).get('secret') ?? '');
    void submit(async () => {
      await credentials.rotate(companyId, credential.id, secret);
      await cache.invalidateQueries({ queryKey: ['credentials', companyId] });
    }, () => setOpen(false));
  }}>
    {error ? <ErrorBanner error={error} /> : null}
    <Field label={t('credentials.secret')} name="secret" type="password" required autoComplete="off" />
    <Button type="submit" busy={busy}>{t('credentials.checkAndSave')}</Button>
    <Button variant="ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
  </form>;
}
